import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { 
  Users, 
  Building2, 
  Calendar, 
  Briefcase, 
  FileText, 
  Clock,
  TrendingUp,
  Cake,
  PartyPopper,
  Gift,
  Trophy,
  Award,
  ClipboardList,
  ListChecks,
} from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar } from "recharts";
import api from "../lib/api";

const Dashboard = () => {
  const { currentBusiness, user } = useAuth();
  const [stats, setStats] = useState({
    total_employees: 0,
    total_departments: 0,
    pending_leaves: 0,
    open_jobs: 0,
    new_applications: 0,
    attendance_today: 0,
    absent_today: 0,
    onboarding_complete: 0,
    onboarding_pending: 0,
    pending_hr_tasks: 0,
  });
  const [activities, setActivities] = useState([]);
  const [celebrations, setCelebrations] = useState({ birthdays: [], anniversaries: [], milestones: [] });
  const [upcomingMilestones, setUpcomingMilestones] = useState([]);
  const [weeklyAttendance, setWeeklyAttendance] = useState([]);
  const [leaveTrends, setLeaveTrends] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      if (!currentBusiness?.id) {
        setLoading(false);
        return;
      }

      try {
        const [statsRes, activitiesRes, celebrationsRes, milestonesRes, weeklyRes, leaveRes] = await Promise.all([
          api.get(`/dashboard/stats?business_id=${currentBusiness.id}`),
          api.get(`/dashboard/recent-activities?business_id=${currentBusiness.id}`),
          api.get(`/dashboard/celebrations?business_id=${currentBusiness.id}`),
          api.get(`/dashboard/upcoming-milestones?business_id=${currentBusiness.id}`),
          api.get(`/dashboard/weekly-attendance?business_id=${currentBusiness.id}`),
          api.get(`/dashboard/leave-trends?business_id=${currentBusiness.id}`)
        ]);
        setStats(statsRes.data);
        setActivities(activitiesRes.data);
        setCelebrations(celebrationsRes.data);
        setUpcomingMilestones(milestonesRes.data.upcoming_milestones);
        setWeeklyAttendance(weeklyRes.data);
        setLeaveTrends(leaveRes.data);
      } catch (error) {
        console.error("Failed to fetch dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentBusiness?.id]);

  const statCards = [
    { 
      title: "Total Employees", 
      value: stats.total_employees, 
      icon: Users, 
      color: "bg-primary/10 text-primary",
    },
    { 
      title: "Departments", 
      value: stats.total_departments, 
      icon: Building2, 
      color: "bg-secondary text-secondary-foreground",
    },
    { 
      title: "Pending Leaves", 
      value: stats.pending_leaves, 
      icon: Calendar, 
      color: "bg-amber-100 text-amber-700",
    },
    { 
      title: "Open Positions", 
      value: stats.open_jobs, 
      icon: Briefcase, 
      color: "bg-accent text-accent-foreground",
    },
    { 
      title: "New Applications", 
      value: stats.new_applications, 
      icon: FileText, 
      color: "bg-blue-100 text-blue-700",
    },
    { 
      title: "Present Today", 
      value: stats.attendance_today, 
      icon: Clock, 
      color: "bg-green-100 text-green-700",
      subtitle: stats.total_employees > 0 ? `${Math.round((stats.attendance_today / stats.total_employees) * 100)}%` : null
    },
    { 
      title: "Onboarding Pending", 
      value: stats.onboarding_pending, 
      icon: ClipboardList, 
      color: "bg-orange-100 text-orange-700",
      subtitle: stats.total_employees > 0 ? `${stats.onboarding_complete} done` : null
    },
    { 
      title: "Pending HR Tasks", 
      value: stats.pending_hr_tasks, 
      icon: ListChecks, 
      color: "bg-rose-100 text-rose-700",
      link: "/hr-tasks",
    },
  ];

  if (!currentBusiness) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center" data-testid="no-business-state">
        <Building2 className="h-16 w-16 text-slate-300 mb-4" />
        <h2 className="text-2xl font-semibold text-slate-800 font-heading mb-2">No Business Selected</h2>
        <p className="text-slate-500 mb-6">Create or select a business to get started</p>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard">
      {/* Welcome Section */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 font-heading">
            Welcome back, {user?.first_name}!
          </h1>
          <p className="text-slate-500 mt-1">
            Here's what's happening at {currentBusiness?.name} today.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="bg-white px-3 py-1">
            <TrendingUp className="h-3 w-3 mr-1 text-green-500" />
            All systems operational
          </Badge>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {statCards.map((stat, index) => (
          <Card 
            key={stat.title} 
            className={`stat-card animate-fade-in${stat.link ? ' cursor-pointer hover:ring-2 hover:ring-primary/20' : ''}`}
            style={{ animationDelay: `${index * 0.1}s` }}
            data-testid={`stat-${stat.title.toLowerCase().replace(/\s/g, '-')}`}
            onClick={stat.link ? () => navigate(stat.link) : undefined}
          >
            <CardContent className="p-0">
              <div className="flex items-start justify-between">
                <div className={`p-2 rounded-xl ${stat.color}`}>
                  <stat.icon className="h-5 w-5" />
                </div>
                {stat.subtitle && (
                  <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                    {stat.subtitle}
                  </span>
                )}
              </div>
              <div className="mt-4">
                <p className="text-3xl font-bold text-slate-800 font-heading">{stat.value}</p>
                <p className="text-sm text-slate-500 mt-1">{stat.title}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Attendance Chart */}
        <Card className="card-soft" data-testid="attendance-chart">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Weekly Attendance</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={weeklyAttendance}>
                <defs>
                  <linearGradient id="colorPresent" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(245, 50%, 60%)" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="hsl(245, 50%, 60%)" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <XAxis dataKey="name" axisLine={false} tickLine={false} className="text-xs" />
                <YAxis axisLine={false} tickLine={false} className="text-xs" />
                <Tooltip 
                  contentStyle={{ 
                    borderRadius: '12px', 
                    border: 'none', 
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)' 
                  }} 
                />
                <Area 
                  type="monotone" 
                  dataKey="present" 
                  stroke="hsl(245, 50%, 60%)" 
                  fillOpacity={1} 
                  fill="url(#colorPresent)" 
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Leave Trends Chart */}
        <Card className="card-soft" data-testid="leave-chart">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Leave Trends</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={leaveTrends}>
                <XAxis dataKey="month" axisLine={false} tickLine={false} className="text-xs" />
                <YAxis axisLine={false} tickLine={false} className="text-xs" />
                <Tooltip 
                  contentStyle={{ 
                    borderRadius: '12px', 
                    border: 'none', 
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)' 
                  }} 
                />
                <Bar 
                  dataKey="leaves" 
                  fill="hsl(330, 70%, 85%)" 
                  radius={[8, 8, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Celebrations Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Birthdays This Week */}
        <Card className="card-soft" data-testid="weekly-birthdays">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-pink-100">
                <Cake className="h-5 w-5 text-pink-600" />
              </div>
              <CardTitle className="font-heading text-lg">Birthdays This Week</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {celebrations.birthdays.length === 0 ? (
              <div className="text-center py-6 text-slate-500">
                <Gift className="h-10 w-10 mx-auto mb-2 text-slate-300" />
                <p>No birthdays this week</p>
              </div>
            ) : (
              <div className="space-y-3">
                {celebrations.birthdays.map((person) => (
                  <div 
                    key={person.id} 
                    className="flex items-center gap-4 p-3 rounded-xl bg-gradient-to-r from-pink-50 to-transparent hover:from-pink-100 transition-colors"
                  >
                    <div className="w-10 h-10 rounded-full bg-pink-100 flex items-center justify-center">
                      <Cake className="h-5 w-5 text-pink-600" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-slate-800">{person.name}</p>
                      <p className="text-sm text-slate-500">{person.day}, {person.date_display}</p>
                    </div>
                    <Badge className="bg-pink-100 text-pink-700 border-pink-200">
                      Birthday
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Work Anniversaries This Week */}
        <Card className="card-soft" data-testid="weekly-anniversaries">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-amber-100">
                <PartyPopper className="h-5 w-5 text-amber-600" />
              </div>
              <CardTitle className="font-heading text-lg">Anniversaries This Week</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {celebrations.anniversaries.length === 0 ? (
              <div className="text-center py-6 text-slate-500">
                <PartyPopper className="h-10 w-10 mx-auto mb-2 text-slate-300" />
                <p>No anniversaries this week</p>
              </div>
            ) : (
              <div className="space-y-3">
                {celebrations.anniversaries.map((person) => (
                  <div 
                    key={person.id} 
                    className="flex items-center gap-4 p-3 rounded-xl bg-gradient-to-r from-amber-50 to-transparent hover:from-amber-100 transition-colors"
                  >
                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                      <PartyPopper className="h-5 w-5 text-amber-600" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-slate-800">{person.name}</p>
                      <p className="text-sm text-slate-500">{person.day}, {person.date_display}</p>
                    </div>
                    <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                      {person.years} {person.years === 1 ? 'Year' : 'Years'}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Milestones This Week */}
        <Card className="card-soft" data-testid="weekly-milestones">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-purple-100">
                <Trophy className="h-5 w-5 text-purple-600" />
              </div>
              <CardTitle className="font-heading text-lg">Milestones This Week</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {celebrations.milestones.length === 0 ? (
              <div className="text-center py-6 text-slate-500">
                <Trophy className="h-10 w-10 mx-auto mb-2 text-slate-300" />
                <p>No milestones this week</p>
              </div>
            ) : (
              <div className="space-y-3">
                {celebrations.milestones.map((person) => (
                  <div 
                    key={person.id} 
                    className="flex items-center gap-4 p-3 rounded-xl bg-gradient-to-r from-purple-50 to-transparent hover:from-purple-100 transition-colors"
                  >
                    <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                      <Trophy className="h-5 w-5 text-purple-600" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-slate-800">{person.name}</p>
                      <p className="text-sm text-slate-500">{person.day}, {person.date_display}</p>
                    </div>
                    <Badge className="bg-purple-100 text-purple-700 border-purple-200">
                      {person.years} Years
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Upcoming Milestones (Next 30 Days) */}
      {upcomingMilestones.length > 0 && (
        <Card className="card-soft" data-testid="upcoming-milestones">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-gradient-to-br from-purple-100 to-indigo-100">
                <Award className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <CardTitle className="font-heading text-lg">Upcoming Service Milestones</CardTitle>
                <p className="text-sm text-slate-500">Employees reaching 3, 5, 8, 10, 15, 20+ years in the next 30 days</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {upcomingMilestones.map((person) => (
                <div 
                  key={person.id} 
                  className="flex items-center gap-4 p-4 rounded-xl border border-purple-100 bg-gradient-to-r from-purple-50/50 to-indigo-50/50 hover:shadow-md transition-all"
                >
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-100 to-indigo-100 flex items-center justify-center">
                    <span className="text-lg font-bold text-purple-700">{person.years}</span>
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-slate-800">{person.name}</p>
                    <p className="text-sm text-slate-500">{person.date_display} ({person.days_until === 0 ? 'Today' : person.days_until === 1 ? 'Tomorrow' : `In ${person.days_until} days`})</p>
                    <Badge className="mt-1 bg-gradient-to-r from-purple-100 to-indigo-100 text-purple-700 border-purple-200">
                      {person.milestone_type}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Activity */}
      <Card className="card-soft" data-testid="recent-activities">
        <CardHeader>
          <CardTitle className="font-heading text-lg">Recent Activity</CardTitle>
        </CardHeader>
        <CardContent>
          {activities.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              No recent activities
            </div>
          ) : (
            <div className="space-y-4">
              {activities.map((activity, index) => (
                <div 
                  key={index} 
                  className="flex items-start gap-4 p-4 rounded-xl hover:bg-slate-50/50 transition-colors"
                >
                  <div className={`p-2 rounded-lg ${
                    activity.type === 'leave' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'
                  }`}>
                    {activity.type === 'leave' ? (
                      <Calendar className="h-4 w-4" />
                    ) : (
                      <FileText className="h-4 w-4" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-slate-700">{activity.message}</p>
                    <p className="text-xs text-slate-400 mt-1">
                      {new Date(activity.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <Badge 
                    variant="outline" 
                    className={`capitalize ${
                      activity.status === 'pending' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                      activity.status === 'approved' || activity.status === 'hired' ? 'bg-green-50 text-green-700 border-green-200' :
                      'bg-slate-50 text-slate-600 border-slate-200'
                    }`}
                  >
                    {activity.status}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
